import type { FunnelStepName } from '../types';

/** The steps in plain words, in the order people take them (docs/admin-analytics-brief.md). */
export const funnelStepWords: Record<FunnelStepName, string> = {
  visited: 'Visited',
  signedUp: 'Signed up',
  firstJob: 'First job',
  setupFinished: 'Setup finished',
  firstPages: 'First pages',
  firstExport: 'First export',
  paid: 'Paid',
};

export function funnelStepWord(step: FunnelStepName): string {
  return funnelStepWords[step];
}

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "2 days", "3 h", "20 min", "under a minute": a span in the unit a person would say. */
export function spanWord(seconds: number): string {
  if (seconds < MINUTE) return 'under a minute';
  if (seconds < HOUR) return `${Math.round(seconds / MINUTE)} min`;
  if (seconds < DAY) return `${Math.round(seconds / HOUR)} h`;
  const days = seconds / DAY;
  const rounded = days < 10 ? Math.round(days * 10) / 10 : Math.round(days);
  return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
}

/** "median 2 days", "median under a minute"; empty when nobody has taken the step. */
export function medianWord(seconds: number | null): string {
  return seconds === null ? '' : `median ${spanWord(seconds)}`;
}

/**
 * The time a step took, labelled with what it is measured from (round 3, marketing should 5):
 * "median 2 days from the step before". Null when there is nothing to say: from visitors, who are
 * not timed, or when nobody went on. When companies went on but none has both dates (they reached
 * the later step without the earlier), it says so, never "nobody yet" beside "2 of 2 went on".
 */
export function gapMedianWord(
  from: FunnelStepName,
  wentOn: number,
  medianSeconds: number | null,
): string | null {
  if (medianSeconds !== null) return `median ${spanWord(medianSeconds)} from the step before`;
  if (from === 'visited' || wentOn === 0) return null;
  return 'not timed: they reached it without the step before';
}

/**
 * First export's two medians in one line: "median 2 days after first pages · 3 days after sign-up".
 * The second is the histogram's median, so the two never disagree.
 */
export function exportMedianWord(
  from: FunnelStepName,
  fromStepBefore: number | null,
  fromSignUp: number | null,
): string | null {
  const parts = [
    fromStepBefore === null
      ? null
      : `${spanWord(fromStepBefore)} after ${funnelStepWord(from).toLowerCase()}`,
    fromSignUp === null ? null : `${spanWord(fromSignUp)} after sign-up`,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? null : `median ${parts.join(' · ')}`;
}

/** "google · ap-launch", or just the source when the campaign only repeats it. */
export function campaignWord(source: string, campaign: string): string {
  return campaign === source || campaign === 'direct' ? source : `${source} · ${campaign}`;
}

/**
 * The one definition of "activated" (critique r2, N3): a company's first export, because the export
 * is where a customer gets value. Today's tile and the funnel say it in these words.
 */
export const activatedHelp = 'Activated means a company’s first export, the moment it gets value.';

/** The one definition of "paid" in the admin area: money, not a plan. */
export const paidHelp =
  'Paid means card money received, never a plan chosen or a package staff set.';

/** The side fact under the funnel: finished job setups, and why they are not a step. */
export function setupWord(count: number, previous: number): string {
  const now = `${count} ${count === 1 ? 'company' : 'companies'} finished a job setup`;
  return `${now} (${previous} the period before). Not a step: a setup is finished before or after the first pages arrive.`;
}

/** The partner filter's word for companies no partner sent and none was linked to at sign-up. */
export const NO_PARTNER = 'none';

/** The partner filter's word for companies a partner sent that have no partner link now. */
export const UNLINKED_PARTNER = 'unlinked';

/**
 * "ACME", "No partner" for the filter's `none`, "Partner, unlinked" for `unlinked`; "Every partner"
 * when not narrowed. The boards filter on the partner at sign-up, never today's link.
 */
export function partnerWord(partner: string | null | undefined): string {
  if (!partner) return 'Every partner';
  if (partner === NO_PARTNER) return 'No partner';
  return partner === UNLINKED_PARTNER ? 'Partner, unlinked' : partner;
}
